import { Form, TextField } from '../components/forms';
/**
 * LOGIN PAGE
 *
 * Supports both native email/password and Google OAuth authentication.
 * Users can sign in and then connect their Shopify stores.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Box,
  Button,
  Divider,
} from '@shopify/polaris';
import { useAuth } from '../App';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com';

export default function Login() {
  const { isAuthenticated, isLoading, refreshAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showVerificationBanner, setShowVerificationBanner] = useState(false);

  // Get error/success from URL params
  const searchParams = new URLSearchParams(location.search);
  const urlError = searchParams.get('error');
  const urlSuccess = searchParams.get('success');
  const orgSlug = searchParams.get('org');
  const switchSignIn = searchParams.get('switch') === '1';
  const requestedRedirect = searchParams.get('redirect') || '/';
  const afterLogin = /^\/invite\/[^/?#]+$/.test(requestedRedirect) ? requestedRedirect : '/';

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated && !switchSignIn && !urlError) {
      navigate(afterLogin);
    }
  }, [isAuthenticated, isLoading, navigate, afterLogin, switchSignIn, urlError]);

  const handleEmailLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const result = await response.json();

      if (!result.success) {
        if (result.code === 'EMAIL_NOT_VERIFIED') {
          setShowVerificationBanner(true);
          setFormError(null);
        } else if (result.code === 'ACCOUNT_LOCKED') {
          setFormError(`Your account is temporarily locked. Try again in ${Math.ceil(result.lockoutMinutes || 15)} minutes.`);
        } else {
          setFormError(result.error || 'Invalid email or password');
        }
        return;
      }

      // Success - refresh auth context and redirect
      await refreshAuth();
      navigate(afterLogin);
    } catch {
      setFormError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [email, password, navigate, refreshAuth, afterLogin]);

  const handleGoogleSignIn = () => {
    const params = new URLSearchParams();
    if (orgSlug) {
      params.set('org', orgSlug);
    }
    if (afterLogin.startsWith('/invite/')) {
      try { params.set('invite', decodeURIComponent(afterLogin.slice('/invite/'.length))); } catch { setFormError('Invalid invitation link'); return; }
    }
    const queryString = params.toString();
    window.location.href = `${API_URL}/api/auth/google${queryString ? `?${queryString}` : ''}`;
  };

  const handleResendVerification = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      const result = await response.json();
      if (result.success) {
        setFormError(null);
        setShowVerificationBanner(false);
        // Show success message
        navigate('/login?success=verification_sent');
      }
    } catch {
      // Silently fail
    }
  };

  const getErrorMessage = (errorCode: string | null): string | null => {
    switch (errorCode) {
      case 'access_denied':
        return 'You cancelled the sign-in process. Please try again.';
      case 'invalid_state':
        return 'Session expired. Please try signing in again.';
      case 'token_exchange_failed':
        return 'Failed to complete sign-in. Please try again.';
      case 'userinfo_failed':
        return 'Could not retrieve your account information. Please try again.';
      case 'user_creation_failed':
        return 'Failed to create your account. Please try again.';
      case 'no_code':
        return 'Authentication failed. Please try again.';
      case 'company_google_needs_setup':
        return 'The company Google connection needs new credentials. Ask the company owner to review its settings.';
      case 'company_sign_in_changed':
        return 'Company sign-in settings changed. Start sign-in again.';
      case 'google_oauth_disabled':
        return 'Google sign-in is not enabled for this organization.';
      case 'email_domain_not_allowed':
        return 'Your email domain is not allowed for this organization.';
      case 'account_link_required':
        return 'An account already uses this email. Sign in with its password, or reset it, then link Google in Settings.';
      case 'email_not_verified':
        return 'Google must confirm your email address before you can sign in.';
      case 'email_conflict':
        return 'This email is already associated with a different account.';
      case 'org_not_found':
        return 'Organization not found.';
      case 'unknown':
        return 'An unexpected error occurred. Please try again.';
      default:
        return errorCode ? `Authentication error: ${errorCode}` : null;
    }
  };

  const getSuccessMessage = (successCode: string | null): string | null => {
    switch (successCode) {
      case 'email_verified':
        return 'Your email has been verified. You can now sign in.';
      case 'password_reset':
        return 'Your password has been reset. Please sign in with your new password.';
      case 'verification_sent':
        return 'Verification email sent. Please check your inbox.';
      case 'registered':
        return 'Account created successfully. Please check your email to verify your account.';
      default:
        return null;
    }
  };

  const errorMessage = urlError ? getErrorMessage(urlError) : formError;
  const successMessage = getSuccessMessage(urlSuccess);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f6f6f7'
      }}>
        <Text as="p" variant="bodyMd" tone="subdued">Loading...</Text>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f6f6f7',
      padding: '20px',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <BlockStack gap="600">
          {/* Logo/Brand */}
          <Box paddingBlockEnd="400">
            <BlockStack gap="200" align="center">
              <Text as="h1" variant="headingXl" alignment="center">
                Whenly
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                Delivery Date & Warehouse Management
              </Text>
            </BlockStack>
          </Box>

          {/* Success Banner */}
          {successMessage && (
            <Banner tone="success" onDismiss={() => navigate('/login')}>
              {successMessage}
            </Banner>
          )}

          {/* Error Banner */}
          {errorMessage && (
            <Banner tone="critical" onDismiss={() => {
              setFormError(null);
              if (urlError) navigate('/login');
            }}>
              {errorMessage}
            </Banner>
          )}

          {/* Verification Needed Banner */}
          {showVerificationBanner && (
            <Banner tone="warning">
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd">
                  Please verify your email address before signing in.
                </Text>
                <Button size="slim" onClick={handleResendVerification}>
                  Resend verification email
                </Button>
              </BlockStack>
            </Banner>
          )}

          {/* Sign In Card */}
          <Card>
            <BlockStack gap="500">
              <Text as="h2" variant="headingMd">
                Sign in to your account
              </Text>

              {/* Email/Password Form */}
              <Form onSubmit={handleEmailLogin}>
                <BlockStack gap="400">
                  <TextField
                    label="Email"
                    type="email"
                    requiredIndicator value={email}
                    onChange={setEmail}
                    autoComplete="email"
                    disabled={isSubmitting}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    requiredIndicator value={password}
                    onChange={setPassword}
                    autoComplete="current-password"
                    disabled={isSubmitting}
                  />
                  <InlineStack align="end">
                    <Link
                      to="/forgot-password"
                      style={{
                        color: '#2C6ECB',
                        textDecoration: 'none',
                        fontSize: '13px',
                      }}
                    >
                      Forgot password?
                    </Link>
                  </InlineStack>
                  <Button
                    submit
                    variant="primary"
                    size="large"
                    fullWidth
                    loading={isSubmitting}
                    disabled={!email || !password}
                  >
                    Sign in
                  </Button>
                </BlockStack>
              </Form>

              {/* Divider */}
              <InlineStack gap="400" align="center" blockAlign="center">
                <div style={{ flex: 1 }}><Divider /></div>
                <Text as="span" variant="bodySm" tone="subdued">or</Text>
                <div style={{ flex: 1 }}><Divider /></div>
              </InlineStack>

              {/* Google Sign In */}
              <button
                onClick={handleGoogleSignIn}
                type="button"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: 500,
                  backgroundColor: '#fff',
                  border: '1px solid #c4cdd5',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s, border-color 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f6f6f7';
                  e.currentTarget.style.borderColor = '#8c9196';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                  e.currentTarget.style.borderColor = '#c4cdd5';
                }}
              >
                <GoogleIcon />
                <span>Continue with Google</span>
              </button>

              {/* Sign Up Link */}
              <Box paddingBlockStart="200">
                <Text as="p" variant="bodyMd" alignment="center">
                  Don't have an account?{' '}
                  <Link
                    to="/register"
                    style={{
                      color: '#2C6ECB',
                      textDecoration: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Create account
                  </Link>
                </Text>
              </Box>

              <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                By signing in, you agree to our Terms of Service and Privacy Policy.
              </Text>
            </BlockStack>
          </Card>

          {/* Help Text */}
          <Text as="p" variant="bodySm" tone="subdued" alignment="center">
            Need help? Contact{' '}
            <a href="mailto:support@whenly.app" style={{ color: '#2C6ECB' }}>
              support@whenly.app
            </a>
          </Text>
        </BlockStack>
      </div>
    </div>
  );
}

// Google Icon Component
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}
